/**
 * Users, matching screen-users in docs/prototype.html.
 *
 * Front Porch staff only. Sign-up is disabled everywhere, so a record created
 * here is the only way anyone reaches the application — which is also why a
 * client administrator cannot open this screen, even for their own client.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { T } from '../lib/copyEdit.tsx';

interface UserClient {
  id: string;
  name: string;
}

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: 'fps_super_admin' | 'client_admin';
  isActive: boolean;
  lastActiveAt: string | null;
  clients: UserClient[];
}

const ROLE_LABEL: Record<UserRow['role'], string> = {
  fps_super_admin: 'FPS Super Admin',
  client_admin: 'Client Admin',
};

/** Initials for the avatar. Two letters at most, so the chip stays a circle. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/** Relative where it reads better, absolute once it stops being useful. */
function lastActive(iso: string | null): string {
  if (!iso) return 'Never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 5) return 'Now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  if (minutes < 60 * 24 * 7) return `${Math.round(minutes / (60 * 24))}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function Users() {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [clients, setClients] = useState<UserClient[]>([]);
  const [staffDomain, setStaffDomain] = useState('frontporchsolutions.com');
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/users');
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as {
        users: UserRow[];
        clients: UserClient[];
        staffEmailDomain: string;
      };
      setUsers(body.users);
      setClients(body.clients);
      setStaffDomain(body.staffEmailDomain);
    } catch {
      setError('Could not load users.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function setActive(row: UserRow, isActive: boolean) {
    setActionError(null);
    const res = await fetch(`/api/users/${row.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setActionError(body.error ?? 'That did not save.');
      return;
    }
    void load();
  }

  /**
   * Sends the welcome email to somebody who already exists.
   *
   * Two needs, one action: a user added quietly before their client was ready,
   * and a user who says the first one never arrived. There is no token in this
   * email, so sending it again costs nothing and invalidates nothing.
   */
  async function sendWelcome(row: UserRow) {
    setActionError(null);
    setNotice(null);

    const res = await fetch(`/api/users/${row.id}/welcome`, { method: 'POST' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setActionError(body.error ?? 'That did not send.');
      return;
    }
    setNotice(`Welcome email sent to ${row.email}.`);
  }

  /**
   * Removes an account outright.
   *
   * Confirmed rather than immediate, because unlike deactivating it cannot be
   * undone. The server refuses when the account is attached to anything, and
   * says what, so the refusal is worth showing verbatim rather than replacing
   * with something generic.
   */
  async function remove(row: UserRow) {
    setActionError(null);
    setNotice(null);

    if (!window.confirm(`Delete ${row.email}? This cannot be undone.`)) return;

    const res = await fetch(`/api/users/${row.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setActionError(body.error ?? 'That did not delete.');
      return;
    }
    setNotice(`${row.email} deleted. That address can be invited again.`);
    void load();
  }

  if (error) return <p className="auth-error">{error}</p>;
  if (!users) return <div className="app-loading" aria-busy="true" />;

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow">Front Porch Solutions</p>
          <h1 className="title"><T k="users.title" d="Users" /></h1>
          <p className="lede"><T k="users.lede" d="Everyone with access, and the clients they manage." /></p>
        </div>
        <div className="pactions">
          {!inviting && !editing && (
            <button type="button" className="btn-compact" onClick={() => setInviting(true)}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add User
            </button>
          )}
        </div>
      </div>

      <div className="callout">
        <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <div>
          Only Front Porch staff create accounts and assign clients. Nobody can sign themselves up,
          so a record here is the only way in. Client admins never provision access.
        </div>
      </div>

      {actionError && <p className="auth-error">{actionError}</p>}
      {notice && <p className="save-note">{notice}</p>}

      {inviting || editing ? (
        <UserForm
          clients={clients}
          staffDomain={staffDomain}
          existing={editing}
          onCancel={() => {
            setInviting(false);
            setEditing(null);
          }}
          onSaved={() => {
            setInviting(false);
            setEditing(null);
            void load();
          }}
        />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th><T k="users.col.user" d="User" /></th>
                <th><T k="users.col.role" d="Role" /></th>
                <th><T k="users.col.clients" d="Clients" /></th>
                <th><T k="users.col.lastActive" d="Last Active" /></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.isActive ? undefined : 'row--off'}>
                  <td>
                    <div className="cell">
                      <span className="av">{initials(u.name)}</span>
                      <div>
                        <b>{u.name}</b>
                        <div className="csub">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${u.role === 'fps_super_admin' ? 'b-info' : 'b-neutral'}`}>
                      <span className="d" />
                      {ROLE_LABEL[u.role]}
                    </span>
                  </td>
                  <td>
                    {u.role === 'fps_super_admin' ? (
                      <span className="cchip"><T k="users.allClients" d="All Clients" /></span>
                    ) : u.clients.length === 0 ? (
                      /* Reachable only through a client being deleted, and it
                         means the person can see nothing. Worth saying. */
                      <span className="csub"><T k="users.noneAssigned" d="None assigned" /></span>
                    ) : (
                      <div className="cchips">
                        {u.clients.map((c) => (
                          <span key={c.id} className="cchip">
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {u.isActive ? (
                      lastActive(u.lastActiveAt)
                    ) : (
                      <span className="badge b-neutral">
                        <span className="d" />
                        Deactivated
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="cedit" onClick={() => setEditing(u)}>
                        Edit
                      </button>
                      {u.isActive && (
                        <button
                          type="button"
                          className="cedit"
                          onClick={() => void sendWelcome(u)}
                        >
                          Send Welcome
                        </button>
                      )}
                      <button
                        type="button"
                        className={`cedit${u.isActive ? ' cedit--danger' : ''}`}
                        onClick={() => void setActive(u, !u.isActive)}
                      >
                        {u.isActive ? 'Deactivate' : 'Reactivate'}
                      </button>
                      {/* Only once deactivated, so removal is always a second
                          deliberate act rather than one click beside the rest.
                          The server refuses if the account is attached to any
                          history and explains which. */}
                      {!u.isActive && (
                        <button
                          type="button"
                          className="cedit cedit--danger"
                          onClick={() => void remove(u)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function UserForm({
  clients,
  staffDomain,
  existing,
  onCancel,
  onSaved,
}: {
  clients: UserClient[];
  staffDomain: string;
  existing: UserRow | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [role, setRole] = useState<UserRow['role']>(existing?.role ?? 'client_admin');
  const [clientIds, setClientIds] = useState<string[]>(existing?.clients.map((c) => c.id) ?? []);

  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  /* On by default, but a real choice: a client's people are often provisioned
     before their first event exists, and telling them then is premature. */
  const [sendWelcome, setSendWelcome] = useState(true);

  const staffOnWrongDomain =
    role === 'fps_super_admin' && email.trim() !== '' && !email.trim().toLowerCase().endsWith(`@${staffDomain}`);

  function toggleClient(id: string) {
    setClientIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setProblem(null);

    const res = await fetch(existing ? `/api/users/${existing.id}` : '/api/users', {
      method: existing ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(
        existing
          ? { name: name.trim(), role, clientIds }
          : { name: name.trim(), email: email.trim(), role, clientIds, sendWelcome },
      ),
    });

    setSaving(false);

    if (res.ok) {
      onSaved();
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { error?: string };
    setProblem(body.error ?? 'That did not save.');
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head">
        <h3>{existing ? `Edit ${existing.name}` : 'Add User'}</h3>
        <span className="hint">
          {existing ? 'changes apply on their next request' : 'they can sign in straight away'}
        </span>
      </div>

      <div className="panel-body">
        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="uname"><T k="users.field.name" d="Full Name" /></label>
            <input
              id="uname"
              className="fi"
              required
              maxLength={160}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="fg">
            <label className="fl" htmlFor="uemail"><T k="users.field.email" d="Email" /></label>
            <input
              id="uemail"
              className="fi"
              type="email"
              required
              maxLength={320}
              value={email}
              /* The address is the identity both sign-in paths match on, so
                 changing it would silently orphan the account rather than
                 rename it. */
              disabled={existing !== null}
              onChange={(e) => setEmail(e.target.value)}
            />
            {existing ? (
              <p className="fdesc"><T k="users.field.emailLocked" d="An address cannot be changed. Add a new user instead." /></p>
            ) : (
              <p className="fdesc"><T k="users.field.emailHint" d="This is how they sign in. It cannot be changed later." /></p>
            )}
          </div>
        </div>

        <div className="fg">
          <label className="fl" htmlFor="urole"><T k="users.field.role" d="Role" /></label>
          <select
            id="urole"
            className="fi"
            style={{ maxWidth: 260 }}
            value={role}
            onChange={(e) => setRole(e.target.value as UserRow['role'])}
          >
            <option value="client_admin">Client Admin</option>
            <option value="fps_super_admin">FPS Super Admin</option>
          </select>
          <p className="fdesc">
            {role === 'fps_super_admin'
              ? `Sees every client and can provision users. Signs in with Google, and needs an @${staffDomain} address.`
              : 'Sees only the clients selected below, and signs in with an emailed link. Cannot add users.'}
          </p>
          {staffOnWrongDomain && (
            <p className="fwarn">
              Front Porch staff must have an @{staffDomain} address. This account would be created
              but could never sign in.
            </p>
          )}
        </div>

        {role === 'client_admin' && (
          <div className="fg">
            <label className="fl">Clients</label>
            <p className="fdesc" style={{ marginTop: 0, marginBottom: 10 }}>
              Everything they can see. Selecting more than one gives them cross-client access.
            </p>
            {clients.length === 0 ? (
              <p className="fwarn">There are no clients yet. Create one first.</p>
            ) : (
              <div className="chips">
                {clients.map((c) => {
                  const on = clientIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`chip${on ? ' on' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleClient(c.id)}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {!existing && (
          <div className="fg">
            <label className="fcheck">
              <input
                type="checkbox"
                checked={sendWelcome}
                onChange={(e) => setSendWelcome(e.target.checked)}
              />
              <span>
                Send a welcome email
                <span className="fdesc">
                  Explains what the application is and how they sign in. You can send it later from
                  the users list instead.
                </span>
              </span>
            </label>
          </div>
        )}

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-compact"
            disabled={saving || staffOnWrongDomain || (role === 'client_admin' && clientIds.length === 0)}
          >
            {saving ? 'Saving…' : existing ? 'Save Changes' : 'Add User'}
          </button>
        </div>
      </div>
    </form>
  );
}
